import { apiFetch } from './client';
import type { Visibility } from './visibility';

/** Every component the Coffee Lab can place. */
export type BlockType =
  | 'kpi'
  | 'statistic'
  | 'line'
  | 'area'
  | 'bar'
  | 'pie'
  | 'donut'
  | 'scatter'
  | 'histogram'
  | 'box'
  | 'heatmap'
  | 'table'
  | 'text'
  | 'filter';

/** What the API says each type needs, so the builder can ask for it. */
export interface BlockTypeInfo {
  key: BlockType;
  name: string;
  description: string;
  /** [minimum, maximum] groupings and measures. */
  dimensions: [number, number];
  metrics: [number, number];
  required_fields: string[];
  is_static: boolean;
  is_filter: boolean;
  default_size: { w: number; h: number };
}

/** One block of a dashboard: what it asks, how to draw it, where it sits. */
export interface DashboardBlock {
  id: string;
  type: BlockType;
  title: string;
  description: string;
  definition: Record<string, unknown>;
  display: {
    value_field?: string | null;
    secondary_field?: string | null;
    x_field?: string | null;
    y_field?: string | null;
    series_field?: string | null;
    value_fields?: string[] | null;
    x_label?: string | null;
    format?: string | null;
    columns?: string[] | null;
    /** Text blocks carry their content here; they ask no question. */
    text?: string | null;
    /** Filter controls: the column they narrow, how, and what they drive. */
    filter_field?: string | null;
    filter_operator?: string | null;
    targets?: string[] | null;
  };
  layout: { x: number; y: number; w: number; h: number };
}

export interface Dashboard {
  id: string;
  title: string;
  description: string;
  template: string;
  dataset: string;
  dataset_version: string;
  blocks: DashboardBlock[];
  block_count: number;
  /** Who may see it, and whether the caller is its owner. */
  visibility: Visibility;
  is_mine: boolean;
  /**
   * Where this was copied from (Phase 22). The id is provenance and is always
   * here; the title and author only while the original is still readable.
   */
  forked_from: string | null;
  forked_from_title: string;
  forked_from_author: string;
  created_at: string;
  updated_at: string;
}

export type DashboardSummary = Omit<Dashboard, 'blocks'>;

export interface DashboardTemplate {
  key: string;
  name: string;
  description: string;
  needs: string[];
  /** Present only when a dataset version was named in the request. */
  fits?: boolean;
  /** Why it does not fit, in the reader's terms. */
  reason?: string;
}

export interface ResultColumn {
  name: string;
  label: string;
  type: string;
  role: 'dimension' | 'metric';
}

export interface AnalysisResult {
  columns: ResultColumn[];
  rows: Record<string, string | number | boolean | null>[];
  row_count: number;
  truncated: boolean;
  duration_ms: number;
  sql: string;
  warnings: string[];
  /**
   * True when this was summarised from data the reader may not see: totals over
   * groups big enough to hide in, with the small ones withheld (Phase 15).
   */
  is_derived: boolean;
  /**
   * True when the backend answered from its result cache rather than running
   * the query again (Phase 31). `duration_ms` is then this answer's own — near
   * zero — rather than the run that filled the cache.
   */
  cached: boolean;
}

export interface BlockResult {
  id: string;
  type: DashboardBlock['type'];
  result?: AnalysisResult;
  error?: { code: string; message: string };
  /** A text block has nothing to run. */
  static?: boolean;
}

/**
 * Error codes the leakage guard answers with (Phase 15).
 *
 * A block it would not answer has not *failed*: the numbers exist and the page
 * is working exactly as intended. Telling a reader something broke would be
 * both untrue and, in its own small way, a leak — the alarming register says
 * "there is something here you are not getting" where the informative one says
 * what the rule is.
 */
export const GUARDED_CODES = new Set([
  'ROW_LEVEL_NOT_ALLOWED',
  'REVEALING_AGGREGATION',
  'IDENTIFYING_DIMENSION',
  'TOO_MANY_DIMENSIONS',
  'TOO_MANY_FILTERS',
  'LEAKAGE_REFUSED',
]);

export function isGuarded(error?: { code: string }): boolean {
  return Boolean(error && GUARDED_CODES.has(error.code));
}

export interface DashboardRun {
  id: string;
  blocks: BlockResult[];
  /** True when every figure here was summarised from data you may not see. */
  is_derived: boolean;
}

export async function listTemplates(datasetVersionId?: string): Promise<DashboardTemplate[]> {
  const query = datasetVersionId ? `?dataset_version=${encodeURIComponent(datasetVersionId)}` : '';
  const body = await apiFetch<{ templates: DashboardTemplate[] }>(`/dashboards/templates${query}`);
  return body.templates;
}

export async function quickBrew(input: {
  datasetVersionId: string;
  template: string;
  title?: string;
}): Promise<Dashboard> {
  return apiFetch<Dashboard>('/dashboards/quick-brew', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      dataset_version: input.datasetVersionId,
      template: input.template,
      ...(input.title ? { title: input.title } : {}),
    }),
  });
}

/**
 * Copy a dashboard onto data of your own.
 *
 * `datasetVersion` is one of *your* versions. Leaving it out asks for the
 * original's — which the backend allows only when you could already read that
 * dataset, and refuses otherwise rather than handing over access.
 */
export async function forkDashboard(id: string, datasetVersion?: string): Promise<Dashboard> {
  return apiFetch<Dashboard>(`/dashboards/${id}/fork`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(datasetVersion ? { dataset_version: datasetVersion } : {}),
  });
}

export async function listDashboards(): Promise<DashboardSummary[]> {
  const body = await apiFetch<{ results: DashboardSummary[] }>('/dashboards');
  return body.results;
}

export async function getDashboard(id: string): Promise<Dashboard> {
  return apiFetch<Dashboard>(`/dashboards/${id}`);
}

/** What a viewer is looking at: one entry per filter control in use. */
export interface AppliedFilter {
  /** The filter block this came from. The backend checks it against that block. */
  block: string;
  field: string;
  operator: string;
  value: unknown;
}

export interface FilterOptions {
  block: string;
  field: string;
  operator: string;
  label: string;
  values: (string | number | boolean | null)[];
  counts: Record<string, number>;
  /** True when the column has more values than the list can carry. */
  truncated: boolean;
}

export async function runDashboard(
  id: string,
  filters: AppliedFilter[] = [],
): Promise<DashboardRun> {
  return apiFetch<DashboardRun>(`/dashboards/${id}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filters.length ? { filters } : {}),
  });
}

export async function getFilterOptions(id: string, blockId: string): Promise<FilterOptions> {
  return apiFetch<FilterOptions>(`/dashboards/${id}/filters/${blockId}/options`);
}

export async function renameDashboard(id: string, title: string): Promise<Dashboard> {
  return apiFetch<Dashboard>(`/dashboards/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
}

export async function listBlockTypes(): Promise<BlockTypeInfo[]> {
  const body = await apiFetch<{ block_types: BlockTypeInfo[] }>('/dashboards/block-types');
  return body.block_types;
}

export async function createDashboard(input: {
  datasetVersionId: string;
  title: string;
}): Promise<Dashboard> {
  return apiFetch<Dashboard>('/dashboards/new', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dataset_version: input.datasetVersionId, title: input.title }),
  });
}

export async function saveBlocks(id: string, blocks: DashboardBlock[]): Promise<Dashboard> {
  return apiFetch<Dashboard>(`/dashboards/${id}/blocks`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blocks }),
  });
}

export async function deleteDashboard(id: string): Promise<void> {
  return apiFetch<void>(`/dashboards/${id}`, { method: 'DELETE' });
}
